import { createContext, useState } from 'react';
import type { RefinedTrace, Span } from '@repo/shared';

interface TraceContextType {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  trace: Array<Span> | null;
  setTrace: React.Dispatch<React.SetStateAction<Array<Span> | null>>;
  traces: Array<RefinedTrace>;
  setTraces: React.Dispatch<React.SetStateAction<Array<RefinedTrace>>>;
  currentTraceIndex: number;
  setCurrentTraceIndex: React.Dispatch<React.SetStateAction<number>>;
  nextTrace: () => void;
  prevTrace: () => void;
  span: Span | null;
  setSpan: React.Dispatch<React.SetStateAction<Span | null>>;
  openDetail: boolean;
  setOpenDetail: React.Dispatch<React.SetStateAction<boolean>>;
  clearData: () => void;
}

export const TraceContext = createContext<TraceContextType>(
  {} as TraceContextType
);

export const TraceProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [trace, setTrace] = useState<Array<Span> | null>(null);
  const [traces, setTraces] = useState<Array<RefinedTrace>>([]);
  const [currentTraceIndex, setCurrentTraceIndex] = useState(0);
  const [span, setSpan] = useState<Span | null>(null);
  const [openDetail, setOpenDetail] = useState(false);

  const nextTrace = () => {
    if (currentTraceIndex < traces.length - 1) {
      const nextIndex = currentTraceIndex + 1;
      setCurrentTraceIndex(nextIndex);
      const _nextTrace = traces[nextIndex]?.trace;
      if (_nextTrace) {
        setTrace(_nextTrace);
        const parentSpan =
          _nextTrace.find((_span) => _span.parentSpanId === null) ||
          _nextTrace[0];
        if (parentSpan) {
          setSpan(parentSpan);
        }
      }
    }
  };

  const prevTrace = () => {
    if (currentTraceIndex > 0) {
      const prevIndex = currentTraceIndex - 1;
      setCurrentTraceIndex(prevIndex);
      const _prevTrace = traces[prevIndex]?.trace;
      if (_prevTrace) {
        setTrace(_prevTrace);
        const parentSpan =
          _prevTrace.find((_span) => _span.parentSpanId === null) ||
          _prevTrace[0];
        if (parentSpan) {
          setSpan(parentSpan);
        }
      }
    }
  };

  const clearData = () => {
    setOpen(false);
    setTrace(null);
    setSpan(null);
    setOpenDetail(false);
  };

  return (
    <TraceContext.Provider
      value={{
        isOpen: open,
        setIsOpen: setOpen,
        trace,
        setTrace,
        traces,
        setTraces,
        currentTraceIndex,
        setCurrentTraceIndex,
        nextTrace,
        prevTrace,
        span,
        setSpan,
        openDetail,
        setOpenDetail,
        clearData,
      }}>
      {children}
    </TraceContext.Provider>
  );
};
